import { lazy, Suspense, useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useRouteScrollRestoration } from '@/hooks/useRouteScrollRestoration';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RefreshCw, FileSpreadsheet } from 'lucide-react';
import { useUserRoles } from '@/hooks/useUserRoles';
import { Skeleton } from '@/components/ui/skeleton';

// Lazy-load heavy components for faster initial route mount
const ProjectTable = lazy(() => import('./ProjectTable').then(m => ({ default: m.ProjectTable })));
const ProjectCards = lazy(() => import('./ProjectCards').then(m => ({ default: m.ProjectCards })));
import { Project } from '@/types/project';
import { ProjectFilters } from './ProjectFilters';
import { useTeamLeadsData } from '@/hooks/useTeamLeadsData';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from '@/components/ui/pagination';
import { QueryErrorBoundary } from '@/components/common/QueryErrorBoundary';
import { useOptimizedProjectsQuery } from '@/hooks/useOptimizedProjectsQuery';
import { useProjectFiltersState } from '@/hooks/useProjectFiltersState';
import { uiLogger } from '@/lib/structuredLogger';
import ProjectStatusPanel from './ProjectStatusPanel';
import { PROJECT_STATUS_ORDER, type ProjectKpiStatus } from '@/constants/projectStatusLabels';

const ProjectList = () => {
  const { user } = useAuth();
  const { data: userRoles = [] } = useUserRoles();
  const { data: teamLeads = [], isLoading: teamLeadsLoading } = useTeamLeadsData();
  
  // Restore scroll position when navigating between tabs
  useRouteScrollRestoration({ key: 'scroll:projects-list', restoreOnlyOnBack: true });
  
  const isAdmin = userRoles.some(role => role.role_code === 'ROLE_ADMIN');

  // View mode state - force cards mode (table temporarily disabled)
  const [viewMode] = useState<'cards'>(() => {
    // Force cards mode - clear any existing table preference
    const saved = localStorage.getItem('projectViewMode');
    if (saved === 'table') {
      localStorage.setItem('projectViewMode', 'cards');
    }
    return 'cards';
  });


  // Enhanced filter state with persistence and validation
  const {
    filters,
    updateFilters,
    clearFilters,
    updateSingleFilter,
    getActiveFiltersCount
  } = useProjectFiltersState();

  // URL-based pagination (same persistence level as filters)
  const [searchParams, setSearchParams] = useSearchParams();
  const PAGE_SIZE = 24;
  
  const currentPage = useMemo(() => {
    const pageParam = searchParams.get('page');
    const parsed = parseInt(pageParam || '1', 10);
    return isNaN(parsed) || parsed < 1 ? 1 : parsed;
  }, [searchParams]);

  const setPage = useCallback((page: number) => {
    setSearchParams(params => {
      if (page <= 1) {
        params.delete('page'); // Clean URL when page = 1
      } else {
        params.set('page', page.toString());
      }
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  // Create a stable key from filter VALUES to avoid resetting on page change
  const filtersKey = useMemo(() => {
    return [
      filters.searchQuery,
      filters.projectGroup,
      (filters.contractYear || []).sort().join(','),
      (filters.projectType || []).sort().join(','),
      filters.teamLead,
      (filters.status || []).sort().join(','),
      filters.company,
      (filters.projectRoleUserIds || []).sort().join(','),
      filters.projectMark,
      (filters.projectRoleTypes || []).sort().join(','),
      filters.lifecycleStageBuild || '',
      filters.lifecycleStageTender || '',
    ].join('|');
  }, [filters]);

  // Reset pagination when filters change (but not when page changes)
  useEffect(() => {
    // Only reset if page > 1 to avoid unnecessary URL updates
    if (currentPage > 1) {
      setSearchParams(params => {
        params.delete('page');
        return params;
      }, { replace: true });
      uiLogger.info('Pagination reset due to filter change', { filtersKey });
    }
  }, [filtersKey]);

  // Use optimized projects query
  const { 
    data: queryResult, 
    isLoading, 
    error, 
    refetch 
  } = useOptimizedProjectsQuery(filters, currentPage, PAGE_SIZE);
  
  const projects = queryResult?.projects || [];
  const totalCount = queryResult?.totalCount || 0;
  const executionTime = queryResult?.executionTime || 0;
  
  // Computed pagination values
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const hasNextPage = currentPage < totalPages;
  const hasPreviousPage = currentPage > 1;
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalCount);



  if (error) {
    return (
      <div className="text-center p-8">
        <p className="text-destructive mb-4">Không thể tải danh sách dự án</p>
        <Button onClick={() => refetch()} variant="outline">
          <RefreshCw className="mr-2 h-4 w-4" />
          Thử lại
        </Button>
      </div>
    );
  }

  return (
    <QueryErrorBoundary onRetry={() => refetch()}>
      <div className="space-y-6">
        <ProjectStatusPanel
          activeStatus={
            filters.status.length === 1 && (PROJECT_STATUS_ORDER as string[]).includes(filters.status[0])
              ? (filters.status[0] as ProjectKpiStatus)
              : null
          }
          onStatusToggle={(status) => {
            const isActive = filters.status.length === 1 && filters.status[0] === status;
            updateFilters({
              ...filters,
              status: isActive ? [] : [status],
              lifecycleStageBuild: status === 'build' && !isActive ? filters.lifecycleStageBuild : '',
              lifecycleStageTender: status === 'tender' && !isActive ? filters.lifecycleStageTender : '',
            });
          }}
          activeBuildStage={(filters.lifecycleStageBuild || null) as any}
          onBuildStageToggle={(stage) => {
            const same = filters.lifecycleStageBuild === stage;
            updateFilters({
              ...filters,
              status: ['build'],
              lifecycleStageBuild: same ? '' : stage,
              lifecycleStageTender: '',
            });
          }}
          activeTenderStage={(filters.lifecycleStageTender || null) as any}
          onTenderStageToggle={(stage) => {
            const same = filters.lifecycleStageTender === stage;
            updateFilters({
              ...filters,
              status: ['tender'],
              lifecycleStageTender: same ? '' : stage,
              lifecycleStageBuild: '',
            });
          }}
          filters={filters}
        />

        <ProjectFilters
          filters={filters}
          onFiltersChange={updateFilters}
          isLoading={teamLeadsLoading}
        />

      {/* Pagination Info with Performance Metrics and View Toggle */}
      {totalCount > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Hiển thị {startItem}-{endItem} của {totalCount} dự án
            {executionTime > 0 && (
              <span className="ml-2 text-xs text-muted-foreground/70">
                ({Math.round(executionTime)}ms)
              </span>
            )}
          </div>
          
          {/* View Mode Toggle - temporarily hidden, cards only */}
        </div>
      )}

      {projects.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <FileSpreadsheet className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">
              {totalCount === 0 ? 'Chưa có dự án nào' : 'Không tìm thấy dự án phù hợp'}
            </h3>
            <p className="text-muted-foreground">
              {totalCount === 0 
                ? 'Chưa có dữ liệu dự án. Liên hệ Admin để đồng bộ dữ liệu.'
                : 'Thử điều chỉnh bộ lọc để xem thêm dự án.'
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <Suspense fallback={
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-5">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="rounded-2xl border border-border/60 overflow-hidden">
                  <Skeleton className="aspect-[4/3] w-full" />
                  <div className="p-4 space-y-3">
                    <Skeleton className="h-5 w-3/4" />
                    <div className="flex gap-1.5">
                      <Skeleton className="h-5 w-16" />
                      <Skeleton className="h-5 w-20" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          }>
            <ProjectCards projects={projects} />
          </Suspense>
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex justify-center">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setPage(currentPage - 1);
                      }}
                      className={!hasPreviousPage ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                  
                  {(() => {
                    // Generate dynamic page numbers with ellipsis
                    const pageNumbers: (number | 'ellipsis')[] = [];
                    if (totalPages <= 7) {
                      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
                    } else {
                      pageNumbers.push(1);
                      if (currentPage > 3) pageNumbers.push('ellipsis');
                      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                        pageNumbers.push(i);
                      }
                      if (currentPage < totalPages - 2) pageNumbers.push('ellipsis');
                      pageNumbers.push(totalPages);
                    }
                    
                    return pageNumbers.map((page, index) => (
                      <PaginationItem key={page === 'ellipsis' ? `ellipsis-${index}` : page}>
                        {page === 'ellipsis' ? (
                          <PaginationEllipsis />
                        ) : (
                          <PaginationLink
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              setPage(page);
                            }}
                            isActive={page === currentPage}
                            className="cursor-pointer"
                          >
                            {page}
                          </PaginationLink>
                        )}
                      </PaginationItem>
                    ));
                  })()}
                  
                  <PaginationItem>
                    <PaginationNext 
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setPage(currentPage + 1);
                      }}
                      className={!hasNextPage ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </>
      )}
      </div>
    </QueryErrorBoundary>
  );
};

export default ProjectList;